import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import type { ThreadSummary } from '@core/tellent-assistant-types';

import {
  injectTellentAssistantActorGetter,
  injectTellentAssistantAuthTokenGetter,
} from './tellent-assistant-endpoint.provider';

export interface ThreadMessagesResponse {
  messages: unknown[];
}

interface MastraThread {
  id: string;
  resourceId: string;
  title: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface MastraThreadsListResponse {
  threads: MastraThread[];
  total: number;
  page: number;
  perPage: number;
  hasMore: boolean;
}

@Injectable({ providedIn: 'root' })
export class TellentAssistantThreadsHttp {
  private readonly http = inject(HttpClient);
  private readonly getToken =
    injectTellentAssistantAuthTokenGetter({ optional: true }) ?? (() => null);
  private readonly getActor = injectTellentAssistantActorGetter({ optional: true }) ?? (() => null);

  public list(baseUrl: string, resourceId: string): Observable<ThreadSummary[]> {
    return this.http
      .get<MastraThreadsListResponse>(`${baseUrl}/api/memory/threads`, {
        headers: this.authHeaders(),
        params: { resourceId },
      })
      .pipe(map(res => res.threads.map(t => this.toThreadSummary(t))));
  }

  public load(baseUrl: string, threadId: string): Observable<ThreadMessagesResponse> {
    return this.http.get<ThreadMessagesResponse>(
      `${baseUrl}/api/memory/threads/${threadId}/messages`,
      { headers: this.authHeaders() },
    );
  }

  public delete(baseUrl: string, threadId: string): Observable<void> {
    return this.http.delete<void>(`${baseUrl}/agui/threads/${threadId}`, {
      headers: this.authHeaders(),
    });
  }

  private toThreadSummary(t: MastraThread): ThreadSummary {
    return {
      id: t.id,
      title: t.title || 'Untitled conversation',
      updatedAt: new Date(t.updatedAt),
    };
  }

  private authHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const token = this.getToken();
    if (token) headers = headers.set('Authorization', token);
    const actor = this.getActor();
    if (actor) headers = headers.set('x-ts-actor', actor);
    return headers;
  }
}
